import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Nav from "../shared/Nav";
import Footer from "../shared/Footer";

const API_URL = `${process.env.REACT_APP_API_URL}/api`;

/* ── Animated counter for stats ── */
function StatItem({ value, label }) {
  return (
    <div style={s.statItem}>
      <span style={s.statValue}>{value}</span>
      <span style={s.statLabel}>{label}</span>
    </div>
  );
}

/* ── Step indicator ── */
function StepDot({ num, active, done, label }) {
  return (
    <div style={s.stepWrapper}>
      <div
        style={{
          ...s.stepDot,
          ...(done ? s.stepDone : active ? s.stepActive : {}),
        }}
      >
        {done ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 7l3.5 3.5L12 3.5"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <span style={s.stepNum}>{num}</span>
        )}
      </div>
      <span
        style={{
          ...s.stepLabel,
          ...(active ? { color: "#4ecca3" } : done ? { color: "#94a3b8" } : {}),
        }}
      >
        {label}
      </span>
    </div>
  );
}

export default function Apply() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("personal");
  const [fieldsValid, setFieldsValid] = useState({
    personal: false,
    resume: false,
    cover: false,
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    linkedin: "",
    portfolio: "",
    coverLetter: "",
  });

  const [cv, setCv] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await fetch(`${API_URL}/jobs/${jobId}`);
        const data = await res.json();
        setJob(data);
      } catch {
        setError("Could not load job details.");
      } finally {
        setLoading(false);
      }
    };
    if (jobId) fetchJob();
    else setLoading(false);
  }, [jobId]);

  useEffect(() => {
    const personalDone = !!(form.name && form.email);
    const resumeDone = !!cv;
    const coverDone = form.coverLetter.length > 20;
    setFieldsValid({
      personal: personalDone,
      resume: resumeDone,
      cover: coverDone,
    });
  }, [form, cv]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = (file) => {
    if (!file) return;
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      setError("Please upload a PDF or Word document (.pdf, .doc, .docx)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File must be under 5MB.");
      return;
    }
    setError("");
    setCv(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.coverLetter) {
      setError(
        "Please fill in all required fields (Name, Email, Cover Letter).",
      );
      setActiveSection(!form.name || !form.email ? "personal" : "cover");
      return;
    }
    if (!cv) {
      setError("Please upload your resume before submitting.");
      setActiveSection("resume");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("experience", form.experience);
      fd.append("linkedin", form.linkedin);
      fd.append("portfolio", form.portfolio);
      fd.append("coverLetter", form.coverLetter);
      fd.append("jobId", jobId);
      fd.append("cv", cv);

      const res = await fetch(`${API_URL}/applications`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Success screen ── */
  if (submitted) {
    const steps = [
      { label: "Received", icon: "✓", done: true },
      { label: "Under Review", icon: "🔍", done: false },
      { label: "Interview", icon: "💬", done: false },
      { label: "Decision", icon: "🎯", done: false },
    ];
    return (
      <>
        <style>{`${fonts}${globalCss}`}</style>
        <Nav />
        <div
          style={{
            ...s.page,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          <div style={s.successOuter}>
            {/* Top accent bar */}
            <div style={s.successAccentBar} />

            <div style={s.successCard}>
              {/* Background orbs */}
              <div style={s.successOrb1} />
              <div style={s.successOrb2} />

              {/* Check icon */}
              <div style={s.successIconWrap}>
                <div style={s.successIconOuter}>
                  <div style={s.successIconInner}>
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                      <path
                        d="M5 15l7 7L25 8"
                        stroke="#fff"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Heading */}
              <div style={s.successBadge}>🎉 You're in the queue!</div>
              <h2 style={s.successTitle}>Application Submitted!</h2>
              <p style={s.successSub}>
                Thank you for applying
                {job ? (
                  <>
                    {" "}
                    for{" "}
                    <span style={{ color: "#0B1F3A", fontWeight: 700 }}>
                      {job.title}
                    </span>
                  </>
                ) : (
                  ""
                )}
                .
              </p>
              <p style={s.successSubSmall}>
                Our team will carefully review your application and reach out
                within{" "}
                <span style={{ color: "#1a7a6e", fontWeight: 600 }}>
                  5–7 business days
                </span>
                .
              </p>

              {/* Info pills */}
              <div style={s.successInfoRow}>
                <div style={s.successInfoPill}>
                  <span style={s.successInfoIcon}>📧</span>
                  Confirmation sent to your email
                </div>
                <div style={s.successInfoPill}>
                  <span style={s.successInfoIcon}>🔒</span>
                  Your data is secure
                </div>
              </div>

              {/* Timeline */}
              <div style={s.successTimelineWrap}>
                <div style={s.successTimelineLabel}>Application Journey</div>
                <div style={s.successTimeline}>
                  {steps.map((step, i) => (
                    <div key={i} style={s.successTimelineStep}>
                      <div
                        style={{
                          ...s.successTimelineNode,
                          ...(i === 0 ? s.successTimelineNodeActive : {}),
                        }}
                      >
                        {i === 0 ? (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M2 7l3 3 7-6"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              color: i === 0 ? "#fff" : "#b0bcc8",
                            }}
                          >
                            {i + 1}
                          </span>
                        )}
                      </div>
                      {i < steps.length - 1 && (
                        <div
                          style={{
                            ...s.successTimelineLine,
                            ...(i === 0 ? s.successTimelineLineDone : {}),
                          }}
                        />
                      )}
                      <div
                        style={{
                          ...s.successTimelineStepLabel,
                          ...(i === 0
                            ? { color: "#1a7a6e", fontWeight: 700 }
                            : {}),
                        }}
                      >
                        {step.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div style={s.successDivider} />

              {/* Actions */}
              <div style={s.successActions}>
                <button
                  style={s.successBtnPrimary}
                  onClick={() => navigate("/careers")}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 4l-4 4 4 4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Browse More Jobs
                </button>
                <button style={s.successBtnGhost} onClick={() => navigate("/")}>
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const completedCount = Object.values(fieldsValid).filter(Boolean).length;

  return (
    <>
      <style>{`${fonts}${globalCss}`}</style>
      <Nav />
      <div style={s.page}>
        {/* ── Hero job banner ── */}
        <div style={s.heroBanner}>
          <div style={s.heroNoise} />
          <div style={s.heroGlow1} />
          <div style={s.heroGlow2} />
          <div style={s.heroContent}>
            <button className="back-link" onClick={() => navigate("/careers")}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to Careers
            </button>

            {loading ? (
              <div style={s.loadingPulse}>Loading position details…</div>
            ) : job ? (
              <div style={s.heroInner}>
                <div style={s.heroBadge} className="fade-in">
                  <span style={s.heroBadgeDot} />
                  Now Accepting Applications
                </div>
                <h1 style={s.heroTitle} className="fade-in-1">
                  {job.title}
                </h1>
                <div style={s.heroMeta} className="fade-in-2">
                  {job.location && (
                    <span style={s.metaChip}>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M7 1C4.79 1 3 2.79 3 5c0 3.25 4 8 4 8s4-4.75 4-8c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 1 1 7 3a1.5 1.5 0 0 1 0 3z"
                          fill="#4ecca3"
                        />
                      </svg>
                      {job.location}
                    </span>
                  )}
                  {job.type && (
                    <span style={s.metaChip}>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <circle
                          cx="7"
                          cy="7"
                          r="5.5"
                          stroke="#4ecca3"
                          strokeWidth="1.4"
                        />
                        <path
                          d="M7 4v3.5l2 1.5"
                          stroke="#4ecca3"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                      {job.type}
                    </span>
                  )}
                  {job.dept && (
                    <span style={s.metaChip}>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <rect
                          x="2"
                          y="5"
                          width="10"
                          height="7"
                          rx="1.5"
                          stroke="#4ecca3"
                          strokeWidth="1.4"
                        />
                        <path
                          d="M5 5V4a2 2 0 0 1 4 0v1"
                          stroke="#4ecca3"
                          strokeWidth="1.4"
                        />
                      </svg>
                      {job.dept}
                    </span>
                  )}
                  {job.salary && (
                    <span style={s.metaChip}>💰 {job.salary}</span>
                  )}
                </div>
                {job.description && (
                  <p style={s.heroDesc} className="fade-in-3">
                    {job.description}
                  </p>
                )}

                <div style={s.heroBottom}>
                  {job.requirements?.length > 0 && (
                    <div style={s.reqList} className="fade-in-3">
                      <div style={s.reqListTitle}>Key Requirements</div>
                      <div style={s.reqGrid}>
                        {job.requirements.map((r, i) => (
                          <div key={i} style={s.reqPill}>
                            <span style={s.reqPillDot} />
                            {r}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={s.heroStats}>
                    <StatItem value="5–7" label="Days to Review" />
                    <div style={s.statDivider} />
                    <StatItem value="100%" label="Confidential" />
                    <div style={s.statDivider} />
                    <StatItem value="Global" label="Opportunities" />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Main layout ── */}
        <div style={s.mainLayout}>
          {/* ── Sidebar ── */}
          <aside style={s.sidebar}>
            <div style={s.sideCard}>
              <div style={s.sideTitle}>Application Progress</div>
              <div style={s.progressBar}>
                <div
                  style={{
                    ...s.progressFill,
                    width: `${(completedCount / 3) * 100}%`,
                  }}
                />
              </div>
              <div style={s.progressLabel}>
                {completedCount} of 3 sections complete
              </div>

              <div style={s.stepsList}>
                <StepDot
                  num={1}
                  active={activeSection === "personal"}
                  done={fieldsValid.personal}
                  label="Personal Info"
                />
                <div style={s.stepConnector} />
                <StepDot
                  num={2}
                  active={activeSection === "resume"}
                  done={fieldsValid.resume}
                  label="Resume / CV"
                />
                <div style={s.stepConnector} />
                <StepDot
                  num={3}
                  active={activeSection === "cover"}
                  done={fieldsValid.cover}
                  label="Cover Letter"
                />
              </div>
            </div>

            <div style={s.tipCard}>
              <div style={s.tipIcon}>💡</div>
              <div style={s.tipTitle}>Pro Tips</div>
              <ul style={s.tipList}>
                {[
                  "Tailor your cover letter to the role",
                  "Keep your resume to 1–2 pages",
                  "Include quantified achievements",
                  "Proofread before submitting",
                ].map((tip, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: 13,
                      color: "#4a5568",
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background: "#1a7a6e",
                        flexShrink: 0,
                        marginTop: 6,
                      }}
                    />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* ── Form card ── */}
          <div style={s.formWrap}>
            <div style={s.formCard}>
              {/* Form header */}
              <div style={s.formHeader}>
                <div style={s.formHeaderLeft}>
                  <h2 style={s.formTitle}>Your Application</h2>
                  <p style={s.formSub}>
                    Fields marked <span style={{ color: "#f87171" }}>*</span>{" "}
                    are required.
                  </p>
                </div>
                <div style={s.formHeaderBadge}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="2"
                      y="3"
                      width="12"
                      height="10"
                      rx="2"
                      stroke="#4ecca3"
                      strokeWidth="1.4"
                    />
                    <path
                      d="M5 7.5h6M5 10h4"
                      stroke="#4ecca3"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  Secure Form
                </div>
              </div>

              <div style={s.formBody}>
                {/* ── Section 1: Personal Info ── */}
                <div
                  style={s.formSection}
                  onFocus={() => setActiveSection("personal")}
                >
                  <div style={s.sectionHeader}>
                    <div style={s.sectionNum}>01</div>
                    <div>
                      <div style={s.sectionTitle}>Personal Information</div>
                      <div style={s.sectionSub}>
                        Tell us a bit about yourself
                      </div>
                    </div>
                    {fieldsValid.personal && (
                      <div style={s.sectionCheck}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2 7l3.5 3.5L12 3.5"
                            stroke="#4ecca3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div style={s.grid2}>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>
                        Full Name <span style={{ color: "#f87171" }}>*</span>
                      </label>
                      <input
                        className="apply-input"
                        placeholder="e.g. Dr. Priya Sharma"
                        value={form.name}
                        onChange={set("name")}
                      />
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>
                        Email Address{" "}
                        <span style={{ color: "#f87171" }}>*</span>
                      </label>
                      <input
                        className="apply-input"
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={set("email")}
                      />
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>Phone Number</label>
                      <input
                        className="apply-input"
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>Years of Experience</label>
                      <input
                        className="apply-input"
                        placeholder="e.g. 3 years"
                        value={form.experience}
                        onChange={set("experience")}
                      />
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>LinkedIn Profile</label>
                      <div style={s.inputWrap}>
                        <span style={s.inputIcon}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <rect
                              x="1"
                              y="1"
                              width="12"
                              height="12"
                              rx="2"
                              stroke="#94a3b8"
                              strokeWidth="1.3"
                            />
                            <path
                              d="M4 6v4M4 4.5v.01M7 10V7.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V10M7 6v4"
                              stroke="#94a3b8"
                              strokeWidth="1.3"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <input
                          className="apply-input has-icon"
                          placeholder="linkedin.com/in/yourname"
                          value={form.linkedin}
                          onChange={set("linkedin")}
                        />
                      </div>
                    </div>
                    <div style={s.fieldGroup}>
                      <label style={s.label}>Portfolio / Website</label>
                      <div style={s.inputWrap}>
                        <span style={s.inputIcon}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <circle
                              cx="7"
                              cy="7"
                              r="5.5"
                              stroke="#94a3b8"
                              strokeWidth="1.3"
                            />
                            <path
                              d="M7 1.5C7 1.5 5 4 5 7s2 5.5 2 5.5M7 1.5C7 1.5 9 4 9 7s-2 5.5-2 5.5M1.5 7h11"
                              stroke="#94a3b8"
                              strokeWidth="1.3"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <input
                          className="apply-input has-icon"
                          placeholder="yourportfolio.com"
                          value={form.portfolio}
                          onChange={set("portfolio")}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={s.sectionSep} />

                {/* ── Section 2: Resume ── */}
                <div
                  style={s.formSection}
                  onFocus={() => setActiveSection("resume")}
                  onClick={() => setActiveSection("resume")}
                >
                  <div style={s.sectionHeader}>
                    <div style={s.sectionNum}>02</div>
                    <div>
                      <div style={s.sectionTitle}>
                        Resume / CV <span style={{ color: "#f87171" }}>*</span>
                      </div>
                      <div style={s.sectionSub}>
                        PDF, DOC, or DOCX — max 5MB
                      </div>
                    </div>
                    {fieldsValid.resume && (
                      <div style={s.sectionCheck}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2 7l3.5 3.5L12 3.5"
                            stroke="#4ecca3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div
                    className={`drop-zone${dragOver ? " over" : ""}${cv ? " has-file" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current.click()}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFile(e.target.files[0])}
                    />
                    {cv ? (
                      <div style={s.fileUploaded}>
                        <div style={s.fileIcon}>
                          <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                          >
                            <rect
                              x="4"
                              y="2"
                              width="16"
                              height="20"
                              rx="2"
                              fill="#0B1F3A"
                            />
                            <rect
                              x="4"
                              y="2"
                              width="16"
                              height="20"
                              rx="2"
                              stroke="#1a7a6e"
                              strokeWidth="1.2"
                            />
                            <path
                              d="M8 9h8M8 13h8M8 17h5"
                              stroke="#4ecca3"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                            />
                            <circle cx="20" cy="20" r="6" fill="#4ecca3" />
                            <path
                              d="M17.5 20l1.5 1.5 3-3"
                              stroke="#0B1F3A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div>
                          <div style={s.fileName}>{cv.name}</div>
                          <div style={s.fileSize}>
                            {(cv.size / 1024).toFixed(0)} KB · Click to replace
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={s.dropContent}>
                        <div style={s.dropIcon}>
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M16 4v16M10 10l6-6 6 6"
                              stroke="#1a7a6e"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6 22v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2"
                              stroke="#1a7a6e"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div style={s.dropTitle}>Drag & drop your resume</div>
                        <div style={s.dropSub}>
                          or{" "}
                          <span style={s.dropLink}>click to browse files</span>
                        </div>
                        <div style={s.dropFormats}>
                          PDF · DOC · DOCX · Max 5MB
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={s.sectionSep} />

                {/* ── Section 3: Cover Letter ── */}
                <div
                  style={s.formSection}
                  onFocus={() => setActiveSection("cover")}
                >
                  <div style={s.sectionHeader}>
                    <div style={s.sectionNum}>03</div>
                    <div>
                      <div style={s.sectionTitle}>
                        Cover Letter <span style={{ color: "#f87171" }}>*</span>
                      </div>
                      <div style={s.sectionSub}>
                        Make a lasting first impression
                      </div>
                    </div>
                    {fieldsValid.cover && (
                      <div style={s.sectionCheck}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M2 7l3.5 3.5L12 3.5"
                            stroke="#4ecca3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div style={{ position: "relative" }}>
                    <textarea
                      className="apply-input"
                      placeholder="Tell us why you're a great fit for this role, your relevant experience, and what excites you about working at Accelia Clinical Solutions…"
                      value={form.coverLetter}
                      onChange={set("coverLetter")}
                      style={{ minHeight: 180 }}
                    />
                    <div style={s.charCount}>
                      {form.coverLetter.length} characters
                    </div>
                  </div>
                </div>

                {/* ── Error ── */}
                {error && (
                  <div style={s.errorBox} className="shake">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle
                        cx="8"
                        cy="8"
                        r="6.5"
                        stroke="#f87171"
                        strokeWidth="1.3"
                      />
                      <path
                        d="M8 5v3.5M8 10.5v.5"
                        stroke="#f87171"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    {error}
                  </div>
                )}

                {/* ── Footer ── */}
                <div style={s.formFooter}>
                  <div style={s.privacyRow}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M7 1L2 3v4c0 3 2.5 5.5 5 6 2.5-.5 5-3 5-6V3L7 1z"
                        stroke="#94a3b8"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p style={s.privacyNote}>
                      Your data is protected and used solely for this
                      application. We never share personal information without
                      consent.
                    </p>
                  </div>
                  <button
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Application
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8h10M9 4l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

/* ── Fonts ── */
const fonts = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;0,9..144,700;1,9..144,400&family=Outfit:wght@300;400;500;600&display=swap');`;

/* ── Global CSS ── */
const globalCss = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .apply-input {
    width: 100%;
    padding: 13px 16px;
    font-size: 14px;
    font-family: 'Outfit', sans-serif;
    background: #F7F9FC;
    border: 1.5px solid #E4EAF0;
    border-radius: 10px;
    color: #1a2940;
    outline: none;
    transition: border-color 0.22s, box-shadow 0.22s, background 0.22s;
  }
  .apply-input:focus {
    border-color: #1a7a6e;
    box-shadow: 0 0 0 3px rgba(26,122,110,0.12);
    background: #fff;
  }
  .apply-input::placeholder { color: #b0bcc8; }
  textarea.apply-input {
    resize: vertical;
    min-height: 180px;
    line-height: 1.7;
    padding-bottom: 32px;
  }
  .apply-input.has-icon { padding-left: 38px; }

  .drop-zone {
    border: 2px dashed #D0DCE8;
    border-radius: 14px;
    padding: 40px 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
    background: #F7F9FC;
    position: relative;
    overflow: hidden;
  }
  .drop-zone::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(26,122,110,0.06) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.25s;
  }
  .drop-zone:hover::before, .drop-zone.over::before { opacity: 1; }
  .drop-zone:hover, .drop-zone.over {
    border-color: #1a7a6e;
    border-style: solid;
    background: rgba(26,122,110,0.03);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(26,122,110,0.1);
  }
  .drop-zone.has-file {
    border-color: #1a7a6e;
    border-style: solid;
    background: rgba(26,122,110,0.04);
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'Outfit', sans-serif;
    padding: 0;
    margin-bottom: 28px;
    transition: color 0.2s, gap 0.2s;
  }
  .back-link:hover { color: #4ecca3; gap: 9px; }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #0B1F3A 0%, #163258 100%);
    color: #fff;
    border: none;
    border-radius: 12px;
    padding: 15px 32px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Outfit', sans-serif;
    transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
    white-space: nowrap;
    position: relative;
    overflow: hidden;
  }
  .submit-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(78,204,163,0.15) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.2s;
  }
  .submit-btn:hover:not(:disabled)::before { opacity: 1; }
  .submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(11,31,58,0.35);
  }
  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
  }

  .fade-in   { animation: fadeUp 0.5s ease both; }
  .fade-in-1 { animation: fadeUp 0.5s ease 0.08s both; }
  .fade-in-2 { animation: fadeUp 0.5s ease 0.16s both; }
  .fade-in-3 { animation: fadeUp 0.5s ease 0.24s both; }
  .shake     { animation: shake 0.4s ease; }

  @media (max-width: 900px) {
    .main-layout { flex-direction: column !important; }
    .sidebar { width: 100% !important; flex-direction: row !important; flex-wrap: wrap !important; gap: 12px !important; }
    .side-card { flex: 1; min-width: 240px; }
    .tip-card { flex: 1; min-width: 200px; }
  }
  @media (max-width: 640px) {
    .form-grid-2 { grid-template-columns: 1fr !important; }
    .hero-inner { padding: 0 !important; }
    .form-footer-inner { flex-direction: column !important; align-items: stretch !important; }
    .submit-btn { width: 100%; justify-content: center; }
    .hero-stats { flex-direction: column !important; gap: 10px !important; }
    .stat-divider { display: none !important; }
  }
`;

/* ── Styles ── */
const s = {
  page: {
    minHeight: "100vh",
    background: "#F0F4F8",
    fontFamily: "'Outfit', sans-serif",
    paddingBottom: 80,
  },

  /* Hero */
  heroBanner: {
    background:
      "linear-gradient(160deg, #061320 0%, #0B1F3A 45%, #0e2847 100%)",
    position: "relative",
    overflow: "hidden",
    paddingBottom: 48,
  },
  heroNoise: {
    position: "absolute",
    inset: 0,
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundSize: "200px",
    pointerEvents: "none",
  },
  heroGlow1: {
    position: "absolute",
    top: -120,
    right: -80,
    width: 500,
    height: 500,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(26,122,110,0.18) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  heroGlow2: {
    position: "absolute",
    bottom: -100,
    left: -60,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(78,204,163,0.07) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  heroContent: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "40px 24px 0",
    position: "relative",
    zIndex: 1,
  },
  heroInner: {},
  loadingPulse: {
    color: "#94a3b8",
    fontSize: 14,
    paddingTop: 32,
    paddingBottom: 32,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: "rgba(78,204,163,0.12)",
    border: "1px solid rgba(78,204,163,0.25)",
    color: "#4ecca3",
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "6px 14px",
    borderRadius: 999,
    marginBottom: 18,
  },
  heroBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#4ecca3",
    boxShadow: "0 0 0 3px rgba(78,204,163,0.3)",
    animation: "pulse 2s infinite",
  },
  heroTitle: {
    fontFamily: "'Fraunces', serif",
    fontSize: "clamp(28px, 4.5vw, 52px)",
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1.1,
    marginBottom: 16,
    letterSpacing: "-0.02em",
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 18,
  },
  metaChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#cbd5e1",
    fontSize: 13,
    fontWeight: 400,
    padding: "5px 12px",
    borderRadius: 999,
  },
  heroDesc: {
    fontSize: 14.5,
    color: "#94a3b8",
    lineHeight: 1.75,
    maxWidth: 620,
    marginBottom: 28,
  },
  heroBottom: {
    display: "flex",
    alignItems: "flex-start",
    gap: 32,
    flexWrap: "wrap",
    paddingTop: 16,
    borderTop: "1px solid rgba(255,255,255,0.07)",
    marginTop: 8,
  },
  reqList: { flex: "1 1 320px" },
  reqListTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#4ecca3",
    marginBottom: 10,
  },
  reqGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  reqPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12.5,
    color: "#cbd5e1",
  },
  reqPillDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#1a7a6e",
    flexShrink: 0,
  },
  heroStats: {
    display: "flex",
    alignItems: "center",
    gap: 24,
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
  },
  statValue: {
    fontFamily: "'Fraunces', serif",
    fontSize: 24,
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 11,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 500,
  },
  statDivider: {
    width: 1,
    height: 36,
    background: "rgba(255,255,255,0.1)",
  },

  /* Main layout */
  mainLayout: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "36px 24px 0",
    display: "flex",
    gap: 24,
    alignItems: "flex-start",
  },

  /* Sidebar */
  sidebar: {
    width: 240,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    position: "sticky",
    top: 24,
  },
  sideCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "22px 20px",
    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
  },
  sideTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 14,
  },
  progressBar: {
    height: 5,
    background: "#E8EEF4",
    borderRadius: 999,
    overflow: "hidden",
    marginBottom: 8,
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #1a7a6e, #4ecca3)",
    borderRadius: 999,
    transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)",
  },
  progressLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 22,
  },
  stepsList: {
    display: "flex",
    flexDirection: "column",
  },
  stepWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  stepDot: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "1.5px solid #D0DCE8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.3s",
  },
  stepActive: {
    border: "1.5px solid #1a7a6e",
    background: "rgba(26,122,110,0.1)",
  },
  stepDone: {
    background: "#1a7a6e",
    border: "1.5px solid #1a7a6e",
  },
  stepNum: {
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
  },
  stepLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: "#64748b",
    transition: "color 0.3s",
  },
  stepConnector: {
    width: 1.5,
    height: 20,
    background: "#E4EAF0",
    marginLeft: 14,
    marginTop: 2,
    marginBottom: 2,
  },
  tipCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "20px",
    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
    border: "1px solid #EEF2F6",
  },
  tipIcon: { fontSize: 22, marginBottom: 10 },
  tipTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#94a3b8",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  tipList: {
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  /* Form */
  formWrap: { flex: 1, minWidth: 0 },
  formCard: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 4px 40px rgba(0,0,0,0.07)",
    overflow: "hidden",
  },
  formHeader: {
    padding: "26px 32px 22px",
    borderBottom: "1px solid #EEF2F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  formHeaderLeft: {},
  formHeaderBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(26,122,110,0.08)",
    color: "#1a7a6e",
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 999,
  },
  formTitle: {
    fontFamily: "'Fraunces', serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#0B1F3A",
    marginBottom: 4,
    letterSpacing: "-0.01em",
  },
  formSub: { fontSize: 13.5, color: "#94a3b8" },
  formBody: { padding: "28px 32px" },

  /* Sections */
  formSection: { outline: "none" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 20,
  },
  sectionNum: {
    fontFamily: "'Fraunces', serif",
    fontSize: 32,
    fontWeight: 300,
    color: "#D0DCE8",
    lineHeight: 1,
    letterSpacing: "-0.02em",
    minWidth: 40,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#0B1F3A",
  },
  sectionSub: {
    fontSize: 12.5,
    color: "#94a3b8",
    marginTop: 2,
  },
  sectionCheck: {
    marginLeft: "auto",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(26,122,110,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionSep: {
    height: 1,
    background: "#EEF2F6",
    margin: "28px 0",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  fieldGroup: {},
  label: {
    display: "block",
    fontSize: 11.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "#64748b",
    marginBottom: 7,
  },
  inputWrap: { position: "relative" },
  inputIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
  },

  /* Drop zone content */
  dropContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  },
  dropIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "rgba(26,122,110,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
    transition: "transform 0.2s",
  },
  dropTitle: { fontSize: 14, fontWeight: 600, color: "#1a2940" },
  dropSub: { fontSize: 13, color: "#64748b" },
  dropLink: { color: "#1a7a6e", fontWeight: 600 },
  dropFormats: {
    fontSize: 11.5,
    color: "#b0bcc8",
    marginTop: 4,
    letterSpacing: "0.05em",
  },

  /* File uploaded */
  fileUploaded: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    justifyContent: "center",
  },
  fileIcon: {},
  fileName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0B1F3A",
    marginBottom: 3,
  },
  fileSize: { fontSize: 12, color: "#94a3b8" },

  /* Char count */
  charCount: {
    position: "absolute",
    bottom: 10,
    right: 12,
    fontSize: 11,
    color: "#b0bcc8",
    pointerEvents: "none",
  },

  /* Error */
  errorBox: {
    marginTop: 20,
    padding: "13px 16px",
    background: "#FFF5F5",
    border: "1px solid #FECACA",
    borderRadius: 10,
    fontSize: 13.5,
    color: "#dc2626",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  /* Footer */
  formFooter: {
    marginTop: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    paddingTop: 24,
    borderTop: "1px solid #EEF2F6",
  },
  privacyRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    maxWidth: 380,
  },
  privacyNote: {
    fontSize: 12,
    color: "#94a3b8",
    lineHeight: 1.6,
    margin: 0,
  },

  /* Buttons */
  btnPrimary: {
    background: "#0B1F3A",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 28px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    transition: "background 0.2s",
  },
  btnOutline: {
    background: "none",
    color: "#0B1F3A",
    border: "2px solid #0B1F3A",
    borderRadius: 10,
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
  },

  /* Success */
  successOuter: {
    width: "100%",
    maxWidth: 520,
    padding: "0 20px",
    animation: "fadeUp 0.5s ease both",
  },
  successAccentBar: {
    height: 5,
    background: "linear-gradient(90deg, #1a7a6e, #4ecca3, #1a7a6e)",
    borderRadius: "999px 999px 0 0",
    marginBottom: -1,
  },
  successCard: {
    background: "#fff",
    borderRadius: "0 0 24px 24px",
    padding: "48px 40px 40px",
    textAlign: "center",
    boxShadow: "0 20px 80px rgba(11,31,58,0.12)",
    position: "relative",
    overflow: "hidden",
  },
  successOrb1: {
    position: "absolute",
    top: -80,
    right: -60,
    width: 280,
    height: 280,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(78,204,163,0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  successOrb2: {
    position: "absolute",
    bottom: -60,
    left: -40,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(26,122,110,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  successIconWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 20,
    position: "relative",
    zIndex: 1,
  },
  successIconOuter: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    background: "rgba(26,122,110,0.08)",
    border: "1.5px solid rgba(26,122,110,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  successIconInner: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1a7a6e 0%, #0B1F3A 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 24px rgba(26,122,110,0.35)",
  },
  successBadge: {
    display: "inline-block",
    background: "rgba(26,122,110,0.08)",
    color: "#1a7a6e",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 14px",
    borderRadius: 999,
    marginBottom: 14,
    letterSpacing: "0.03em",
    position: "relative",
    zIndex: 1,
  },
  successTitle: {
    fontFamily: "'Fraunces', serif",
    fontSize: 34,
    fontWeight: 700,
    color: "#0B1F3A",
    marginBottom: 12,
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
    position: "relative",
    zIndex: 1,
  },
  successSub: {
    fontSize: 15,
    color: "#64748b",
    lineHeight: 1.6,
    margin: "0 0 6px",
    position: "relative",
    zIndex: 1,
  },
  successSubSmall: {
    fontSize: 13.5,
    color: "#94a3b8",
    lineHeight: 1.6,
    margin: "0 0 24px",
    position: "relative",
    zIndex: 1,
  },
  successInfoRow: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: 28,
    position: "relative",
    zIndex: 1,
  },
  successInfoPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#F7F9FC",
    border: "1px solid #E4EAF0",
    borderRadius: 999,
    padding: "7px 14px",
    fontSize: 12.5,
    color: "#64748b",
    fontWeight: 500,
  },
  successInfoIcon: { fontSize: 13 },
  successTimelineWrap: {
    background: "#F7F9FC",
    border: "1px solid #EEF2F6",
    borderRadius: 16,
    padding: "20px 24px",
    marginBottom: 28,
    position: "relative",
    zIndex: 1,
  },
  successTimelineLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 18,
  },
  successTimeline: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    position: "relative",
  },
  successTimelineStep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    position: "relative",
    gap: 8,
  },
  successTimelineNode: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#E4EAF0",
    border: "2px solid #D0DCE8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    position: "relative",
    flexShrink: 0,
  },
  successTimelineNodeActive: {
    background: "linear-gradient(135deg, #1a7a6e, #4ecca3)",
    border: "2px solid #1a7a6e",
    boxShadow: "0 0 0 4px rgba(78,204,163,0.2)",
  },
  successTimelineLine: {
    position: "absolute",
    top: 15,
    left: "50%",
    width: "100%",
    height: 2,
    background: "#E4EAF0",
    zIndex: 0,
  },
  successTimelineLineDone: {
    background: "linear-gradient(90deg, #4ecca3, #E4EAF0)",
  },
  successTimelineStepLabel: {
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 500,
    textAlign: "center",
    lineHeight: 1.3,
  },
  successDivider: {
    height: 1,
    background: "#EEF2F6",
    margin: "0 0 24px",
    position: "relative",
    zIndex: 1,
  },
  successActions: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    flexWrap: "wrap",
    position: "relative",
    zIndex: 1,
  },
  successBtnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "linear-gradient(135deg, #0B1F3A 0%, #163258 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "13px 28px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 4px 16px rgba(11,31,58,0.25)",
  },
  successBtnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    color: "#64748b",
    border: "1.5px solid #E4EAF0",
    borderRadius: 12,
    padding: "13px 28px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    transition: "border-color 0.2s, color 0.2s",
  },
};
